import type { Coordinate, PlaybackPoint, PlaybackSegment, VehicleStatus } from "./models";

/** A silence this long between two recorded points is its own "offline"
 *  segment -- the backend's own rule for calling a device offline. */
export const OFFLINE_GAP_MS = 10 * 60_000;

/** The longest playback the backend serves in one request. */
export const MAX_PLAYBACK_RANGE_MS = 7 * 24 * 60 * 60_000;

interface OpenSegment {
  readonly status: VehicleStatus;
  readonly startMs: number;
  readonly startLocation: string | null;
  endLocation: string | null;
  readonly startPoint: Coordinate;
  endPoint: Coordinate;
  speeds: number[];
  alertCount: number;
}

const label = (point: PlaybackPoint) => point.locationLabel || null;
const where = ({ latitude, longitude }: PlaybackPoint): Coordinate => ({ latitude, longitude });

/**
 * The S6 segments, from the recorded points alone (the endpoint sends no
 * segments): consecutive points with the same status form one segment, which
 * lasts until the next status starts; a gap longer than `gapMs` is an
 * "offline" segment of its own. Average speed is for moving segments only --
 * `null` (N/A) with fewer than two points -- and 0 when parked.
 */
export function derivePlaybackSegments(
  vehicleId: string,
  points: readonly PlaybackPoint[],
  gapMs: number = OFFLINE_GAP_MS,
): PlaybackSegment[] {
  const segments: PlaybackSegment[] = [];
  let open: OpenSegment | null = null;
  let lastMs = 0;
  let lastLocation: string | null = null;
  let lastPoint: Coordinate | null = null;

  const close = (endMs: number) => {
    if (open === null) return;
    const moving = open.status === "moving";
    segments.push({
      id: `${vehicleId}-segment-${String(segments.length + 1)}`,
      status: open.status,
      startAt: new Date(open.startMs).toISOString(),
      endAt: new Date(endMs).toISOString(),
      startLocation: open.startLocation,
      endLocation: open.endLocation,
      startPoint: open.startPoint,
      endPoint: open.endPoint,
      alertCount: open.alertCount,
      averageSpeedKph: !moving
        ? open.status === "offline"
          ? null
          : 0
        : open.speeds.length < 2
          ? null
          : Math.round(open.speeds.reduce((sum, speed) => sum + speed, 0) / open.speeds.length),
    });
    open = null;
  };

  for (const point of points) {
    const atMs = Date.parse(point.recordedAt);
    if (!Number.isFinite(atMs)) continue;

    if (open !== null && atMs - lastMs > gapMs) {
      // The device went quiet: what came before ends at its last point.
      close(lastMs);
      open = {
        status: "offline",
        startMs: lastMs,
        startLocation: lastLocation,
        endLocation: label(point),
        startPoint: lastPoint ?? where(point),
        endPoint: where(point),
        speeds: [],
        alertCount: 0,
      };
      close(atMs);
    }

    if (open === null || open.status !== point.status) {
      close(atMs);
      open = {
        status: point.status,
        startMs: atMs,
        startLocation: label(point),
        endLocation: label(point),
        startPoint: where(point),
        endPoint: where(point),
        speeds: [],
        alertCount: 0,
      };
    }

    open.speeds.push(point.speedKph);
    open.alertCount += point.alerts.length;
    open.endLocation = label(point) ?? open.endLocation;
    open.endPoint = where(point);
    lastMs = atMs;
    lastLocation = label(point);
    lastPoint = where(point);
  }

  close(lastMs);
  return segments;
}
